import React from 'react';
import { SEOData } from '~/cards/seo/types';
import H from '@zola/zola-ui/src/typography/Headings';
import { SEO_COLORS } from '~/cards/constants/seoColors';
import { getCardSettings } from '~/cards/util/cardSettingsUtils';
import { CARD_SHAPE_FILTERS } from '~/cards/constants/CardShapeOptions';
import type { CardSettings } from '~/cards/types/cardSettings';
import { getTitleCase } from '../CardListingRedesign/utils';
import { SEOSchema } from './SEOSchema';
import type { Motif } from '../types';
import { SEOBlocksWrapper, SEOBlock, SEOCopy, SEOList, SEOReviews } from './SEOContentBlock.styles';

type InternalLinksProps = {
  cardTypeSettings: CardSettings;
  baseUrl: string;
  lowercaseLabel: string;
  allMotifs?: Motif[];
};

const StyleInternalLinks: React.FC<InternalLinksProps> = ({
  cardTypeSettings,
  baseUrl,
  lowercaseLabel,
  allMotifs,
}) => {
  const { label = '' } = cardTypeSettings;
  return allMotifs?.length ? (
    <SEOBlock>
      <H.Title3 presentation="h4">{label} for every style</H.Title3>
      <p>
        From bohemian and botanical to minimalist and modern, there are gorgeous {lowercaseLabel} of
        all styles on Zola.
      </p>
      <SEOList>
        {allMotifs.map((motif) => (
          <a href={`${baseUrl}/${motif.key.toLowerCase()}`} key={motif.key}>
            {motif.name} {lowercaseLabel}
          </a>
        ))}
      </SEOList>
    </SEOBlock>
  ) : null;
};

const ColorInternalLinks: React.FC<InternalLinksProps> = ({ baseUrl, lowercaseLabel }) => {
  return (
    <SEOBlock>
      <H.Title3 presentation="h4">Colors that match your theme</H.Title3>
      <p>
        Whether you&apos;re going for classic white or a bright array of hues, Zola has{' '}
        {lowercaseLabel} of every color to match your wedding theme.
      </p>
      <SEOList>
        {SEO_COLORS.map((color) => (
          <a href={`${baseUrl}/${color.toLowerCase()}`} key={color}>
            {getTitleCase(color === 'BEIGE' ? 'OFF_WHITE' : color)} {lowercaseLabel}
          </a>
        ))}
      </SEOList>
    </SEOBlock>
  );
};

const ShapeInternalLinks: React.FC<InternalLinksProps> = ({
  cardTypeSettings,
  baseUrl,
  lowercaseLabel,
}) => {
  const { label = '', hasShapes } = cardTypeSettings;
  return hasShapes ? (
    <SEOBlock>
      <H.Title3 presentation="h4">{label} of all shapes & sizes</H.Title3>
      <p>
        From traditional square {lowercaseLabel} to fancy arches, Zola has a variety of card shapes
        to choose from.
      </p>
      <SEOList>
        {CARD_SHAPE_FILTERS.map((shape) => (
          <a href={`${baseUrl}/${shape.value}`} key={shape.value}>
            {shape.label} {lowercaseLabel}
          </a>
        ))}
      </SEOList>
    </SEOBlock>
  ) : null;
};

const FeatureInternalLinks: React.FC<InternalLinksProps> = ({
  cardTypeSettings,
  baseUrl,
  lowercaseLabel,
}) => {
  const {
    label = '',
    hasLetterpress,
    hasMultipleOrientations,
    hasFoil,
    hasMagnet,
    hasNoCustomPhoto,
    hasCustomPhoto,
    isVowRenewal,
  } = cardTypeSettings;

  const showBlock =
    hasFoil ||
    hasLetterpress ||
    hasMagnet ||
    isVowRenewal ||
    hasCustomPhoto ||
    hasNoCustomPhoto ||
    hasMultipleOrientations;

  return showBlock ? (
    <SEOBlock>
      <H.Title3 presentation="h4">Mix and match options</H.Title3>
      <p>
        With so many features and finishes available, choosing {lowercaseLabel} can seem daunting.
        Narrow your search by combining your favorite design options.
      </p>
      <SEOList>
        {hasFoil && <a href={`${baseUrl}?features=foil`}>Foil {lowercaseLabel}</a>}
        {hasLetterpress && (
          <a href={`${baseUrl}?is_letterpress=is_letterpress`}>Letterpress {lowercaseLabel}</a>
        )}
        {hasMagnet && <a href={`${baseUrl}?features=magnet`}>{label} magnets</a>}
        {isVowRenewal && (
          <a href={`${baseUrl}?receptionOnly=elopementandvowrenewal`}>
            Reception-only {lowercaseLabel}
          </a>
        )}
        {hasCustomPhoto && <a href={`${baseUrl}?features=customphoto`}>Photo {lowercaseLabel}</a>}
        {hasNoCustomPhoto && (
          <a href={`${baseUrl}?features=nocustomphoto`}>{label} without photos</a>
        )}
        {hasMultipleOrientations && (
          <>
            <a href={`${baseUrl}?orientations=portrait`}>Portrait {lowercaseLabel}</a>
            <a href={`${baseUrl}?orientations=landscape`}>Landscape {lowercaseLabel}</a>
          </>
        )}
      </SEOList>
    </SEOBlock>
  ) : null;
};

type SEOContentBlockProps = {
  seoData: SEOData;
  activeCardEvent: string;
  allMotifs?: Motif[];
  className?: string;
  isDigital?: boolean;
};

export const SEOContentBlock: React.FC<SEOContentBlockProps> = ({
  seoData,
  activeCardEvent,
  allMotifs = [],
  isDigital = false,
  className,
}) => {
  if (!seoData) return null;

  const { h2, description, summary, faqHeading, faqContent, reviews } = seoData;

  // @ts-expect-error activeCardEvent should be a CardType
  const cardTypeSettings = getCardSettings(activeCardEvent, isDigital);
  const { label = '', urlSlug = '' } = cardTypeSettings;

  const lowercaseLabel = label?.toLowerCase() || '';
  const baseUrl = urlSlug ? `/wedding-planning/${urlSlug}/shop` : '';
  const showInternalLinks = !!cardTypeSettings && !!baseUrl && activeCardEvent !== 'PAPER_ADD_ONS';

  return (
    <SEOBlocksWrapper className={className}>
      <SEOSchema seoData={seoData} />
      <SEOBlock>
        <H.TitleAccent2 presentation="h3">{h2}</H.TitleAccent2>
        <SEOCopy>{summary || description}</SEOCopy>
      </SEOBlock>
      {reviews && (
        <SEOBlock>
          <H.Title3 presentation="h4">What people are saying about Zola {lowercaseLabel}</H.Title3>
          <SEOReviews>{reviews}</SEOReviews>
        </SEOBlock>
      )}
      {!!faqContent && faqContent.length > 0 && (
        <SEOBlock>
          <H.TitleAccent3>{faqHeading}</H.TitleAccent3>
          {faqContent.map((faqItem, index) => (
            <SEOCopy key={index}>
              <H.Title4>{faqItem.subheading}</H.Title4>
              {faqItem.body}
            </SEOCopy>
          ))}
        </SEOBlock>
      )}
      {showInternalLinks && (
        <>
          <StyleInternalLinks
            cardTypeSettings={cardTypeSettings}
            baseUrl={baseUrl}
            lowercaseLabel={lowercaseLabel}
            allMotifs={allMotifs}
          />
          <ColorInternalLinks
            cardTypeSettings={cardTypeSettings}
            baseUrl={baseUrl}
            lowercaseLabel={lowercaseLabel}
          />
          <ShapeInternalLinks
            cardTypeSettings={cardTypeSettings}
            baseUrl={baseUrl}
            lowercaseLabel={lowercaseLabel}
          />
          <FeatureInternalLinks
            cardTypeSettings={cardTypeSettings}
            baseUrl={baseUrl}
            lowercaseLabel={lowercaseLabel}
          />
        </>
      )}
    </SEOBlocksWrapper>
  );
};
